import type { SelectOption } from '@polyseme/design-system';

import { getAge, getYear } from '@polyseme/utils/date/date-utils';
import { useTranslations } from 'next-intl';

const MAX_RETIREMENT_LEAVING_AGE = 71;

const getLegalRetirementAge = (year: number): number => {
  if (year < 1956) return 60;
  if (year >= 1956 && year <= 1963) return 62;
  if (year >= 1964 && year <= 1967) return 63;
  return 64;
};

export const canBeRetired = (dateOfBirth: string): boolean => {
  const yearOfBirth = getYear(dateOfBirth);
  const age = getAge(dateOfBirth);

  return age >= getLegalRetirementAge(yearOfBirth);
};

type GetRetirementAgeRangeArgs = {
  dateOfBirth: string;
  isRetired: boolean;
};
export const getRetirementAgeRange = ({
  dateOfBirth,
  isRetired,
}: GetRetirementAgeRangeArgs) => {
  const yearOfBirth = getYear(dateOfBirth);
  const age = getAge(dateOfBirth);

  const legalRetirementAge = getLegalRetirementAge(yearOfBirth);

  if (isRetired) {
    return {
      minAge: legalRetirementAge,
      maxAge: age,
    };
  }

  return {
    minAge: Math.max(legalRetirementAge, age),
    maxAge: MAX_RETIREMENT_LEAVING_AGE,
  };
};

type UseRetirementAgeOptionsArgs = {
  dateOfBirth?: string;
  isRetired: boolean;
};
export const useRetirementAgeOptions = ({
  dateOfBirth,
  isRetired,
}: UseRetirementAgeOptionsArgs) => {
  const t = useTranslations('landing.common');

  if (!dateOfBirth) return [];

  const { maxAge, minAge } = getRetirementAgeRange({ dateOfBirth, isRetired });

  const options: SelectOption[] = [];

  for (let age = minAge; age <= maxAge; age++) {
    options.push({ label: t('year', { value: age }), value: String(age) });
  }

  return options;
};
